import { useState } from "react";
import { Loader2, Handshake } from "lucide-react";
import { toast } from "sonner";
import { Section } from "./Section";
import { Reveal } from "./Reveal";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { submitLead, apiError } from "../../lib/api";

const inputCls = "bg-forest-dark/60 border-paper/20 text-paper placeholder:text-paper/40 focus-visible:ring-gold h-11";

export const ChannelPartners = () => {
  const [form, setForm] = useState({ name: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type: "channel_partner", ...form });
      setDone(true);
      toast.success("Welcome aboard! Our partnerships team will call you.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section
      id="partners"
      dark
      overline="Channel partners"
      title="Sell the address Bhiwani is waiting for"
      sub="Approved project, bank tie-up, completion-linked payment plan — a product your clients will thank you for."
    >
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="flex h-full flex-col justify-center">
            <Handshake className="h-9 w-9 text-gold" strokeWidth={1.5} />
            <ul className="mt-6 space-y-4">
              {[
                "Ready inventory across 77 plots and 10 sheds",
                "Govt.-approved project — clean paperwork your clients can verify",
                "Bank of Maharashtra loan tie-up makes closures easier",
                "Dedicated partner support from the Bhiwani site office",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-paper/80">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span className="text-base leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="border border-paper/15 bg-forest-light/40 p-6 sm:p-8" data-testid="partner-form-card">
            {done ? (
              <div className="py-10 text-center" data-testid="partner-success">
                <p className="font-display text-2xl text-paper">Dhanyavaad!</p>
                <p className="mt-2 text-sm text-paper/65">Our partnerships team will reach out within a working day.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="channel-partner-form">
                <h3 className="font-display text-2xl font-medium text-paper">Become a channel partner</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-paper/80" htmlFor="cp-name">Name *</Label>
                    <Input id="cp-name" data-testid="partner-name-input" required minLength={2} className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-paper/80" htmlFor="cp-phone">Phone *</Label>
                    <Input id="cp-phone" data-testid="partner-phone-input" required type="tel" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-paper/80" htmlFor="cp-company">Firm / Agency</Label>
                  <Input id="cp-company" data-testid="partner-company-input" className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Your firm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-paper/80" htmlFor="cp-message">Areas you cover</Label>
                  <Textarea id="cp-message" data-testid="partner-message-input" rows={3} className="bg-forest-dark/60 border-paper/20 text-paper placeholder:text-paper/40 focus-visible:ring-gold" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Bhiwani, Hisar, Rohtak, Delhi NCR…" />
                </div>
                <Button type="submit" disabled={loading} data-testid="partner-submit-button" className="w-full h-11 bg-gold text-forest-dark hover:bg-paper hover:text-forest transition-colors duration-200">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Register as Partner
                </Button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </Section>
  );
};
